import React, { useState } from 'react';
import { createContext, useContext } from 'react';
import NavigationTabs from './NavigationTabs';
import LocationHeader from './LocationHeader';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { FaArrowRight } from 'react-icons/fa';
import clsx from 'clsx';

interface InventoryAccountContextProps {
  mode: 'view' | 'edit';
  setMode: (mode: 'view' | 'edit') => void;
  selected: string[];
  onSelect: (value: string, checked: boolean) => void;
}

interface InventoryAccountProviderProps {
  children: React.ReactNode;
}

const InventoryAccountContext = createContext<InventoryAccountContextProps>({
  mode: 'view',
  setMode: () => {},
  selected: [],
  onSelect: () => {},
});

export const InventoryAccountProvider: React.FC<
  InventoryAccountProviderProps
> = ({ children }) => {
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [selected, setSelected] = useState<string[]>([]);

  const onSelect = (value: string, checked: boolean) =>
    setSelected((prev) => {
      if (checked) return [...prev, value];
      return prev.filter((item) => item !== value);
    });

  return (
    <InventoryAccountContext.Provider
      value={{ mode, setMode, selected, onSelect }}
    >
      <LocationHeader />
      {mode === 'view' && <NavigationTabs />}
      {mode === 'edit' && (
        <div
          className="fixed inset-0 z-[99] cursor-pointer bg-background-base-overlay"
          onMouseDown={() => setMode('view')}
        />
      )}
      <div className={clsx('relative', { 'z-[99]': mode === 'edit' })}>
        {children}
        {mode === 'edit' && !!selected.length && (
          <div className="fixed bottom-0 z-[999] -ml-8 flex h-28 w-full items-center bg-[linear-gradient(0deg,rgba(25,25,37,0.7),rgba(34,39,49,0))] p-10">
            <GraniteButton size="large">
              Open repair for ({selected.length}) services <FaArrowRight />
            </GraniteButton>
          </div>
        )}
      </div>
    </InventoryAccountContext.Provider>
  );
};

export const useInventoryAccount = () => {
  const context = useContext(InventoryAccountContext);
  if (!context) {
    throw new Error(
      'useInventoryAccount must be used within a InventoryAccountProvider',
    );
  }
  return context;
};
